import { AccountingCoinsStackBold } from '@ha/icons';
import React, { useState, useEffect, useContext } from 'react';
import dayjs from 'dayjs';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { AlertLabs, Link, units } from '@ha/dsl';
import { useLocation } from 'ha/helpers/router';
import { useCookie } from 'ha/pages/Search/hooks/useCookie';
import { SearchFiltersContext } from 'ha/pages/Search/SearchFiltersContext';
import { getFilterValues } from 'ha/pages/Search/selectors/filterSelectors';
import { ContractTypeValue } from 'ha/types/SearchFilters';

const useStyles = makeStyles(() => ({
  container: {
    marginBottom: units.rem(1),
  },
}));

const isTheLastDayInMonth = (date: string) => {
  const d = dayjs(date);

  return d.date() === d.daysInMonth();
};

export const SearchResultsNotification: React.FC = () => {
  const { T } = useIntl();
  const { lg } = useMediaQuery();
  const classes = useStyles();

  const { onChangeContractType, filtersCount } =
    useContext(SearchFiltersContext);

  const {
    query: { startDate, endDate },
  } = useLocation();

  const [cookie, updateCookie] = useCookie('use_contract_type_filter');
  const { contractType } = useSelector(getFilterValues);

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (contractType.includes(ContractTypeValue.Daily)) {
      setIsOpen(false);
      return;
    }

    if (cookie) {
      setIsOpen(false);
      return;
    }

    if (!startDate || !endDate) {
      setIsOpen(false);
      return;
    }

    if (dayjs(startDate).date() === 1 && isTheLastDayInMonth(endDate)) {
      setIsOpen(false);
      return;
    }

    if (!filtersCount.contractType?.daily) {
      setIsOpen(false);
      return;
    }

    setIsOpen(true);
  }, [contractType, cookie, startDate, endDate, filtersCount]);

  const handleOnClose = React.useCallback(() => {
    updateCookie('accepted', {
      expires: dayjs().add(1, 'day').toDate(),
    });

    setIsOpen(false);
  }, []);

  const handleOnClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      onChangeContractType([...contractType, ContractTypeValue.Daily]);

      handleOnClose();
    },
    [contractType, onChangeContractType, handleOnClose],
  );

  if (isOpen) {
    return (
      <AlertLabs
        icon={lg ? <AccountingCoinsStackBold /> : false}
        severity="notification"
        onClose={handleOnClose}
        className={classes.container}
      >
        {`${T('search.info_bar.rental_contract.daily')} `}
        <Link to="" kind="mint-underline" onClick={handleOnClick}>
          {T(
            'search.info_bar.rental_contract.daily_link',
            filtersCount.contractType?.daily,
          )}
        </Link>
      </AlertLabs>
    );
  }

  return null;
};

export default SearchResultsNotification;
